import React, { useState, useEffect, useRef } from 'react';
import { DataTable, DataTableExpandedRows, DataTableRowEvent, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { Menubar } from 'primereact/menubar';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import { Message } from 'primereact/message';
import { vendorsApi } from '../../api';
import { Vendor, Check } from '../../types';
import { useDataTableState } from '../../hooks';
import { formatDate, formatCurrency } from '../../utils';

interface VendorsTabProps {
  clientId: string;
}

export const VendorsTab: React.FC<VendorsTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const tableRef = useRef<DataTable<Vendor[]>>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [sourceVendor, setSourceVendor] = useState<Vendor | null>(null);
  const [targetVendor, setTargetVendor] = useState<Vendor | null>(null);
  const [isMerging, setIsMerging] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [editName, setEditName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Row expansion state
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>();
  const [checkMap, setCheckMap] = useState<Record<string, Check[]>>({});
  const [expansionLoading, setExpansionLoading] = useState<Record<string, boolean>>({});

  // Persistent filter and sort state
  const {
    sortField,
    sortOrder,
    handleSort,
    filters,
    handleFilter: onFilterChange,
  } = useDataTableState({
    storageKey: 'customerIdentification:vendors',
    defaultSort: { field: 'name', order: 1 },
    defaultFilters: {
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  useEffect(() => {
    loadVendors();
  }, [clientId]);

  const loadVendors = async () => {
    try {
      setIsLoading(true);
      const data = await vendorsApi.getVendors(clientId);
      setVendors(data);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load vendors',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateVendor = async () => {
    if (!newVendorName.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Vendor name is required',
        life: 3000,
      });
      return;
    }

    try {
      setIsCreating(true);
      const created = await vendorsApi.createVendor(clientId, {
        name: newVendorName,
      });
      setVendors([created, ...vendors]);
      setShowCreateDialog(false);
      setNewVendorName('');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Vendor created successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to create vendor',
        life: 5000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleMergeVendors = async () => {
    if (!sourceVendor || !targetVendor) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select both source and target vendors',
        life: 3000,
      });
      return;
    }

    if (sourceVendor.id === targetVendor.id) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Cannot merge a vendor with itself',
        life: 3000,
      });
      return;
    }

    try {
      setIsMerging(true);
      await vendorsApi.mergeVendors(sourceVendor.id, targetVendor.id);
      setVendors(vendors.filter((v) => v.id !== sourceVendor.id));
      // Clear cached checks and collapse expanded rows so fresh data is fetched on next expand
      setCheckMap((prev) => {
        const updated = { ...prev };
        delete updated[sourceVendor.id];
        delete updated[targetVendor.id];
        return updated;
      });
      setExpandedRows({});
      setShowMergeDialog(false);
      setSourceVendor(null);
      setTargetVendor(null);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Vendors merged successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to merge vendors',
        life: 5000,
      });
    } finally {
      setIsMerging(false);
    }
  };

  const handleEditVendor = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setEditName(vendor.name);
    setShowEditDialog(true);
  };

  const handleUpdateVendor = async () => {
    if (!editingVendor || !editName.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Vendor name is required',
        life: 3000,
      });
      return;
    }

    try {
      setIsUpdating(true);
      const updated = await vendorsApi.updateVendor(editingVendor.id, { name: editName });
      setVendors(vendors.map((v) => (v.id === updated.id ? updated : v)));
      setShowEditDialog(false);
      setEditingVendor(null);
      setEditName('');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Vendor updated successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to update vendor',
        life: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteVendor = (vendor: Vendor) => {
    confirmDialog({
      message: `Are you sure you want to delete vendor "${vendor.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await vendorsApi.deleteVendor(vendor.id);
          setVendors(vendors.filter((v) => v.id !== vendor.id));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Vendor deleted successfully',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to delete vendor',
            life: 5000,
          });
        }
      },
    });
  };

  const actionBodyTemplate = (rowData: Vendor) => {
    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-edit"
          link
          tooltip="Edit"
          onClick={() => handleEditVendor(rowData)}
        />
        <Button
          icon="ms-out ms-out-delete"
          link
          tooltip="Delete"
          onClick={() => handleDeleteVendor(rowData)}
        />
      </div>
    );
  };

  const handleRowExpand = (event: DataTableRowEvent) => {
    const vendor = event.data as Vendor;

    if (checkMap[vendor.id] || expansionLoading[vendor.id]) {
      return;
    }

    setExpansionLoading((prev) => ({ ...prev, [vendor.id]: true }));

    vendorsApi
      .getVendorChecks(clientId, vendor.id)
      .then((checks) => {
        setCheckMap((prev) => ({ ...prev, [vendor.id]: checks }));
      })
      .catch((err) => {
        console.error('Failed to fetch checks for vendor', vendor.id, ':', err);
        setCheckMap((prev) => ({ ...prev, [vendor.id]: [] }));
      })
      .finally(() => {
        setExpansionLoading((prev) => ({ ...prev, [vendor.id]: false }));
      });
  };

  const onPage = (e: DataTablePageEvent) => {
    setFirst(e.first);
    setRows(e.rows);
    setTimeout(() => {
      const wrapper = tableRef.current?.getElement()?.querySelector('.p-datatable-wrapper');
      if (wrapper) {
        wrapper.scrollTop = 0;
      }
    }, 0);
  };

  const rowExpansionTemplate = (vendor: Vendor) => {
    const checks = checkMap[vendor.id] || [];
    const isLoading = expansionLoading[vendor.id];

    if (isLoading) {
      return <ProgressBar mode="indeterminate" />;
    }

    if (checks.length === 0) {
      return (
        <div className="row-expansion-container">
          <Message severity="info" text="No checks assigned to this vendor." />
        </div>
      );
    }

    return (
      <div className="row-expansion-container">
        <Panel header="Checks">
          <DataTable value={checks} size="small">
            <Column field="account_name" header="Account" style={{ width: '20%' }} />
            <Column
              field="check_date"
              header="Date"
              style={{ width: '12%' }}
              body={(rowData: Check) => formatDate(rowData.check_date)}
            />
            <Column field="check_number" header="Check #" style={{ width: '10%' }} />
            <Column
              field="amount"
              header="Amount"
              style={{ width: '12%' }}
              body={(rowData: Check) => formatCurrency(rowData.amount)}
            />
            <Column field="payee_name" header="Payee" style={{ width: '20%' }} />
            <Column field="memo" header="Memo" />
          </DataTable>
        </Panel>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <Menubar
        className="actions-menubar"
        model={[
          {
            label: 'New Vendor',
            icon: 'ms-out ms-out-add',
            command: () => setShowCreateDialog(true),
          },
          {
            label: 'Merge Vendors',
            icon: 'ms-out ms-out-merge',
            command: () => setShowMergeDialog(true),
          },
        ]}
      />

      <DataTable
        ref={tableRef}
        value={vendors}
        dataKey="id"
        loading={isLoading}
        paginator
        first={first}
        rows={rows}
        onPage={onPage}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> vendors
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="flex"
        filterDisplay="menu"
        filters={filters}
        onFilter={onFilterChange}
        emptyMessage="No vendors found"
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={rowExpansionTemplate}
        onRowExpand={handleRowExpand}
      >
        <Column expander style={{ width: '3%' }} />
        <Column
          field="name"
          header="Name"
          sortable
          style={{ width: '25%' }}
          filter
          filterPlaceholder="Search name"
          showFilterMatchModes={false}
        />
        <Column
          field="qbo_vendor_id"
          header="In QB?"
          sortable
          style={{ width: '15%' }}
          body={(rowData) => {
            if (rowData.qbo_vendor_id) {
              return (
                <i className="ms-out ms-out-check-circle ms-success"></i>
              );
            }
            return <span className="secondary-text">-</span>;
          }}
        />
        <Column
          field="created_at"
          header="Created"
          body={(rowData) => new Date(rowData.created_at).toLocaleDateString()}
          sortable
          style={{ width: '15%' }}
        />
        <Column body={actionBodyTemplate} />
      </DataTable>

      {/* Create Vendor Dialog */}
      <Dialog
        header="Create New Vendor"
        visible={showCreateDialog}
        className="assign-dialog"
        onHide={() => {
          setShowCreateDialog(false);
          setNewVendorName('');
        }}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowCreateDialog(false)}
            />
            <Button
              label="Create"
              severity="success"
              onClick={handleCreateVendor}
              loading={isCreating}
            />
          </div>
        }
      >
        <div className="dialog-form">
          <div className="field">
            <label htmlFor="vendorName">Vendor Name *</label>
            <InputText
              id="vendorName"
              value={newVendorName}
              onChange={(e) => setNewVendorName(e.target.value)}
              placeholder="Enter vendor name"
              className="full-width-input"
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      {/* Merge Vendors Dialog */}
      <Dialog
        header="Merge Vendors"
        visible={showMergeDialog}
        className="assign-dialog"
        onHide={() => {
          setShowMergeDialog(false);
          setSourceVendor(null);
          setTargetVendor(null);
        }}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowMergeDialog(false)}
            />
            <Button
              label="Merge"
              severity="success"
              onClick={handleMergeVendors}
              loading={isMerging}
              disabled={!sourceVendor || !targetVendor}
            />
          </div>
        }
      >
        <div className="dialog-form">
          <p className="mb-3">
            Select the vendor to merge (source) and the vendor to merge into (target).
            All checks from the source vendor will be reassigned to the target vendor.
          </p>
          <div className="field">
            <label htmlFor="sourceVendor">Source Vendor (will be deleted)</label>
            <Dropdown
              id="sourceVendor"
              value={sourceVendor}
              options={vendors}
              onChange={(e) => setSourceVendor(e.value)}
              optionLabel="name"
              placeholder="Select source vendor"
              className="full-width-input"
              filter
            />
          </div>
          <div className="field">
            <label htmlFor="targetVendor">Target Vendor (will keep)</label>
            <Dropdown
              id="targetVendor"
              value={targetVendor}
              options={vendors}
              onChange={(e) => setTargetVendor(e.value)}
              optionLabel="name"
              placeholder="Select target vendor"
              className="full-width-input"
              filter
            />
          </div>
        </div>
      </Dialog>

      {/* Edit Vendor Dialog */}
      <Dialog
        header="Edit Vendor"
        visible={showEditDialog}
        className="assign-dialog"
        onHide={() => {
          setShowEditDialog(false);
          setEditingVendor(null);
          setEditName('');
        }}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowEditDialog(false)}
            />
            <Button
              label="Save"
              severity="success"
              onClick={handleUpdateVendor}
              loading={isUpdating}
            />
          </div>
        }
      >
        <div className="dialog-form">
          <div className="field">
            <label htmlFor="editVendorName">Vendor Name *</label>
            <InputText
              id="editVendorName"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter vendor name"
              className="full-width-input"
              autoFocus
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};
